import { useAuthContext } from '@/auth/useAuthContext';
import { type FeatureFlag, hasFeatureOnClient } from '@/lib/features';
import type { ReactNode } from 'react';
import { UpgradePrompt } from './UpgradePrompt';

interface Props {
  flag: FeatureFlag;
  children: ReactNode;
}

/**
 * Client-side mirror of the server's `requireFeature` middleware.
 *
 * Renders `children` when the active account's tier ships the feature;
 * otherwise renders `<UpgradePrompt>`. Falls closed when there's no
 * active account (signed-out / loading) — the prompt is the right
 * default landing page for an unauthenticated user hitting a gated
 * surface anyway.
 *
 * The server still enforces the gate; this component just removes the
 * UI before the user clicks into a 402.
 */
export function RequireFeature({ flag, children }: Props) {
  const { state } = useAuthContext();
  const tier = state.me?.activeAccount?.tier;
  if (hasFeatureOnClient(tier, flag)) return <>{children}</>;
  return <UpgradePrompt feature={flag} />;
}
